import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, MapPin, CalendarDays, Clock, Users, UserPlus, Check, Trophy, ArrowLeft, Share2 } from "lucide-react";

const SPORT_COLORS = {
  basketball: "bg-orange-100 text-orange-700",
  volleyball: "bg-blue-100 text-blue-700",
  badminton: "bg-green-100 text-green-700",
  tennis: "bg-yellow-100 text-yellow-700",
  football: "bg-emerald-100 text-emerald-700",
  soccer: "bg-emerald-100 text-emerald-700",
};

export default function JoinBooking() {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/bookings/shared/${shareToken}`);
        setData(res.data);
        if (res.data.already_joined) setJoined(true);
      } catch (err) {
        setError(err.response?.data?.detail || "Link not found or expired");
      }
      setLoading(false);
    };
    load();
  }, [shareToken]);

  // Pre-fill name if logged in
  useEffect(() => {
    if (user && !name) setName(user.name || "");
    if (user && !contact) setContact(user.email || "");
  }, [user]);

  const handleJoin = async () => {
    if (!name.trim()) { toast.error("Please enter your name"); return; }
    if (!user && !contact.trim()) { toast.error("Please enter your email or phone"); return; }
    setJoining(true);
    try {
      await api.post(`/bookings/shared/${shareToken}/join`, {
        name: name.trim(),
        contact: contact.trim(),
      });
      setJoined(true);
      toast.success("You're in! See you on the court!");
      // Refresh data
      const res = await api.get(`/bookings/shared/${shareToken}`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to join");
    }
    setJoining(false);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Loader2 className="w-10 h-10 animate-spin text-court-orange mb-4" />
        <p className="font-heading text-lg uppercase tracking-wide text-muted-foreground">Loading invite...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-slate-50 to-white px-6 text-center">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
          <Share2 className="w-8 h-8 text-red-500" />
        </div>
        <h1 className="font-heading text-2xl font-bold uppercase tracking-tight mb-2">Link Unavailable</h1>
        <p className="text-muted-foreground mb-6 max-w-sm">{error}</p>
        <Button onClick={() => navigate("/")} className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase text-xs px-8">
          Go to SetPlay
        </Button>
      </div>
    );
  }

  const spotsRemaining = data.spots_remaining;
  const isHost = user && user.user_id === data.host_user_id;
  const isFull = spotsRemaining <= 0;
  const isExpired = data.booking_status === "CANCELLED" || data.booking_status === "EXPIRED";
  const sportColor = SPORT_COLORS[data.sport_type?.toLowerCase()] || "bg-slate-100 text-slate-700";

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50/60 to-white">
      {/* Header */}
      <div className="bg-white border-b-2 border-slate-100 px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button onClick={() => navigate("/")} className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <p className="text-[10px] font-heading uppercase tracking-[0.2em] text-orange-500">SetPlay Invite</p>
            <p className="font-heading text-sm font-bold uppercase tracking-tight">Group Booking</p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-5">
        {/* Booking Info Card */}
        <div className="bg-white border-2 border-slate-200 overflow-hidden" data-testid="booking-info-card">
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-5 py-4 text-white">
            <div className="flex items-center gap-2 mb-1">
              <Trophy className="w-4 h-4" />
              <span className="text-xs font-heading uppercase tracking-[0.15em] opacity-90">Join the Game</span>
            </div>
            <h2 className="font-heading text-xl font-bold uppercase tracking-tight">{data.venue_name}</h2>
            <p className="text-sm opacity-90">{data.resource_name}</p>
          </div>
          <div className="px-5 py-4 space-y-3">
            <div className="flex items-center gap-3">
              <Badge className={`${sportColor} font-heading text-xs uppercase px-3 py-1`}>{data.sport_type}</Badge>
              {isExpired && <Badge className="bg-red-100 text-red-700 font-heading text-xs uppercase">Cancelled</Badge>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 text-sm">
                <CalendarDays className="w-4 h-4 text-orange-500 shrink-0" />
                <span className="font-medium">{data.booking_date}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-orange-500 shrink-0" />
                <span className="font-medium">{data.booking_start} - {data.booking_end}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="w-4 h-4 text-orange-500 shrink-0" />
              <span>Hosted by <strong className="text-foreground">{data.host_name}</strong></span>
            </div>
          </div>
        </div>

        {/* Spots Indicator */}
        <div className="bg-white border-2 border-slate-200 px-5 py-4" data-testid="spots-indicator">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-orange-500" />
              <span className="font-heading text-sm font-bold uppercase tracking-tight">Squad</span>
            </div>
            <span className="text-xs font-heading uppercase tracking-[0.15em] text-muted-foreground">
              {data.spots_taken + 1}/{data.max_players} players
            </span>
          </div>
          {/* Spots bar */}
          <div className="flex gap-1 mb-3">
            {Array.from({ length: data.max_players }).map((_, i) => (
              <div key={i}
                className={`h-2 flex-1 rounded-full transition-colors ${
                  i === 0 ? "bg-orange-500" : 
                  i <= data.spots_taken ? "bg-orange-300" : "bg-slate-100"
                }`}
                title={i === 0 ? "Host" : i <= data.spots_taken ? "Joined" : "Open"}
              />
            ))}
          </div>
          {/* Members list */}
          <div className="space-y-2">
            {/* Host */}
            <div className="flex items-center gap-3 py-2 border-b border-slate-100">
              <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center font-heading text-xs font-bold">
                {data.host_name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">{data.host_name}</p>
                <p className="text-[10px] text-orange-500 font-heading uppercase">Host</p>
              </div>
            </div>
            {/* Members */}
            {(data.members || []).map((m, i) => (
              <div key={m.member_id} className="flex items-center gap-3 py-2 border-b border-slate-50">
                <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center font-heading text-xs font-bold">
                  {m.name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{m.name}</p>
                  <p className="text-[10px] text-muted-foreground">{m.user_id ? "SetPlay Member" : "Guest"}</p>
                </div>
                <Check className="w-4 h-4 text-green-500" />
              </div>
            ))}
            {/* Empty spots */}
            {spotsRemaining > 0 && Array.from({ length: Math.min(spotsRemaining, 3) }).map((_, i) => (
              <div key={`empty-${i}`} className="flex items-center gap-3 py-2 opacity-40">
                <div className="w-8 h-8 rounded-full border-2 border-dashed border-slate-300 flex items-center justify-center">
                  <UserPlus className="w-3.5 h-3.5 text-slate-400" />
                </div>
                <p className="text-sm text-muted-foreground">Open spot</p>
              </div>
            ))}
            {spotsRemaining > 3 && (
              <p className="text-xs text-muted-foreground text-center py-1">+{spotsRemaining - 3} more open spots</p>
            )}
          </div>
        </div>

        {/* Join Form */}
        {!joined && !isHost && !isFull && !isExpired && (
          <div className="bg-white border-2 border-orange-200 px-5 py-5 space-y-4" data-testid="join-form">
            <h3 className="font-heading text-sm font-bold uppercase tracking-[0.1em]">Join this game</h3>
            <Input
              data-testid="join-name-input"
              placeholder="Your name"
              value={name}
              onChange={e => setName(e.target.value)}
              className="h-11 border-2 rounded-none font-medium"
              disabled={!!user}
            />
            {!user && (
              <Input
                data-testid="join-contact-input"
                placeholder="Email or phone number"
                value={contact}
                onChange={e => setContact(e.target.value)}
                className="h-11 border-2 rounded-none"
              />
            )}
            {user && (
              <p className="text-xs text-muted-foreground">Joining as <strong>{user.email}</strong></p>
            )}
            <Button
              data-testid="join-btn"
              onClick={handleJoin}
              disabled={joining}
              className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-heading uppercase text-sm tracking-wide shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]"
            >
              {joining ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
              {joining ? "Joining..." : "Join the Squad"}
            </Button>
          </div>
        )}

        {/* Already Joined / Success State */}
        {joined && !isHost && (
          <div className="bg-green-50 border-2 border-green-200 px-5 py-5 text-center" data-testid="join-success">
            <div className="w-12 h-12 rounded-full bg-green-500 text-white flex items-center justify-center mx-auto mb-3">
              <Check className="w-6 h-6" />
            </div>
            <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-green-800">You're In!</h3>
            <p className="text-sm text-green-700 mt-1">See you at {data.venue_name} on {data.booking_date}</p>
            {user && (
              <Button onClick={() => navigate("/bookings")} variant="outline"
                className="mt-4 rounded-full border-2 border-green-300 text-green-700 hover:bg-green-100 font-heading uppercase text-xs">
                Go to My Bookings
              </Button>
            )}
          </div>
        )}

        {/* Host View */}
        {isHost && (
          <div className="bg-orange-50 border-2 border-orange-200 px-5 py-4 text-center" data-testid="host-notice">
            <p className="font-heading text-sm font-bold uppercase text-orange-700">You are the host of this booking</p>
            <p className="text-xs text-orange-600 mt-1">Share this link with friends to fill your squad!</p>
          </div>
        )}

        {/* Full */}
        {isFull && !joined && !isHost && (
          <div className="bg-slate-50 border-2 border-slate-200 px-5 py-5 text-center" data-testid="full-notice">
            <Users className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <h3 className="font-heading text-base font-bold uppercase text-slate-600">Squad is Full</h3>
            <p className="text-sm text-muted-foreground mt-1">All spots for this game have been taken.</p>
          </div>
        )}

        {/* Expired */}
        {isExpired && (
          <div className="bg-red-50 border-2 border-red-200 px-5 py-4 text-center" data-testid="expired-notice">
            <p className="font-heading text-sm font-bold uppercase text-red-700">This booking has been cancelled</p>
          </div>
        )}

        {/* CTA to browse */}
        <div className="text-center pt-2">
          <Button onClick={() => navigate("/search")} variant="link" className="text-orange-500 font-heading uppercase text-xs">
            Browse Venues on SetPlay
          </Button>
        </div>
      </div>
    </div>
  );
}
